<template>
  <div class="tree-menu">

    <!-- Item -->
    <div v-if="prettyDisplay && !data.masked"
         :style="indent"
         class="tree-menu-item">
      <label :id="getNumbering()" :class="{ 'show-range': !hasChildren }" >{{ getNumbering() }} {{ data.name }}</label>
    </div>

    <div v-if="!prettyDisplay"
         :style="indent"
         class="tree-menu-item"
         @click="collapse" >
      <span v-if="!hasChildren" class="custom-control custom-checkbox">
        <Checkbox ref="check" :id="getNumbering()" :checked="isChecked" @click.prevent="click">{{ getNumbering() }} {{ $t(name) }}</Checkbox>
      </span>
      <span v-else>
        <span :class="state.checked == state.children ? 'text-gray-900' : 'text-gray-500'" @click.stop.prevent="checkBranch"><fa :icon="icon" fixed-width/></span>
        {{ getNumbering() }} {{ $t(name) }}
      </span>

      <!-- Arrow (if has children) -->
      <fa v-if="hasChildren" :icon="isCollapsed ? 'angle-down' : 'angle-right'" fixed-width/>
    </div>

    <!-- Children -->
    <CheckableTreeViewItem v-for="(child, id) in children"
                           v-if="isCollapsed"
                           ref="treeView"
                           :data="child"
                           :form-type="formType"
                           :depth="depth + 1"
                           :description="child.description"
                           :flat="flat"
                           :index="id"
                           :key="child.name"
                           :numbering="getNumbering()"
                           :parent="this"
                           :pretty-display="prettyDisplay" />
  </div>
</template>

<style scoped>
/* Fix for checkboxes label */
.custom-control-label {
  padding-top: 2px;
}
.tree-menu-item {
  padding: 0.2em 1em;
}
</style>

<script>
import Vue from 'vue'

export default {
  name: 'CheckableTreeViewItem',

  props: {
    formType: String,
    data: null, // Data tree.
    depth: 0, // Depth we're working on.
    flat: false, // Flatten the tree.
    focus: null, // Focussed node (others are hidden).
    index: 0, // Index of this node from parent.
    numbering: '', // Real numbering (includes root numbering).
    collapsed: false,
    prettyDisplay: false // display colapsing items, checkboxes and so on.
  },

  data: () => ({
    internalCollapsedState: false,
    state: { 'children': 0, 'checked': 0 }
  }),

  computed: {
    allChildrenSelected () {
      const state = this.state // branchState(this.children);
      return this.hasChildren && state.checked > 0 && state.children == state.checked
    },

    children () {
      return this.data.children
    },

    gap () {
      return this.flat ? 0 : this.depth
    },

    hasChildren () {
      return this.data.children.length > 0
    },

    icon () {
      const state = this.branchState(this.children)

      if (state.checked == 0) {
        return ['far', 'square'] // Outline square.
      }

      return ['fas', 'square'] // Solid square.
    },

    id () {
      return this.numbering.split('.').join('')
    },

    indent () {
      return {
        paddingLeft: `${this.gap * 40 + 10}px`
      }
    },

    isCollapsed () {
      return this.internalCollapsedState && this.hasChildren
    },

    isChecked () {
      return !this.data.masked
    },

    masked () {
      return this.data.masked
    },

    name () {
      if (this.depth > 0) {
        return `evaluations.entries.${this.formType}.titles.${this.data.id}`
      }
      return this.$t('evaluations.tabs.' + this.formType)
    }
  },

  watch: {
    collapsed: function (newV, oldV) {
      if (newV) {
        this.internalCollapsedState = newV
      }
    }
  },

  mounted () {
    this.internalCollapsedState = this.collapsed // || (this.hasChildren && this.data.children.reduce(this.recursiveReducer, false));
    this.state = this.branchState(this.children)
  },

  methods: {
    branchState (children) {
      const state = children.reduce(this.reducerStrict, { 'children': 0, 'checked': 0 })

      for (let child of children) {
        const branchState = this.branchState(child.children)
        state.children += branchState.children
        state.checked += branchState.checked
      }

      this.state = state

      return state
    },

    checkBranch () {
      const state = this.allChildrenSelected

      this.data.masked = state

      if (this.hasChildren) {
        this.updateBranchState(this.children, state)
      }

      this.$forceUpdate()
    },

    click (e) {
      this.data.masked = !this.data.masked
    },

    collapse () {
      this.internalCollapsedState = !this.internalCollapsedState
    },

    /**
     * Returns visible nodes.
     *
     * @param List List of previous nodes.
     * @return List of visible nodes.
     */
    getMask (node, list) {
      list.push(node.children.reduce(this.recursiveReducer, false) ? '1' : '0')
      // if (!node.masked && node.children.length > 0) {
      //   node.children.forEach((e) => {
      //     this.getMask(e, list);
      //   });
      // }
      return this.retrieveBranchMask(node.children, list)
    },

    /**
     * Returns numbering of node.
     *
     * @return Current numbering
     */
    getNumbering () {
      if (this.depth == 0) {
        return this.numbering
      }

      // In prop : "`${numbering}.${(index + 1)}.`"
      return this.numbering + (this.index + 1) + '.'
    },

    refresh () {
      if (this.$refs.treeView) {
        this.$nextTick(function () {
          for (let i = 0; i < this.$refs.treeView.length; i++) {
            const treeView = this.$refs.treeView[i]
            if (treeView && treeView[0]) {
              treeView[0].refresh()
            }
          }

          // this.internalCollapsedState = (this.hasChildren && this.data.children.reduce(this.recursiveReducer, false));
        })
      }
    },

    reducer (accumulator, current) {
      return accumulator || !current.masked
    },

    reducerStrict (accumulator, current) {
      // Only operate on leafs.
      if (current.children.length == 0) {
        accumulator.children++
        accumulator.checked += !current.masked ? 1 : 0
      }

      return accumulator
    },

    recursiveReducer (accumulator, current) {
      if (current.children.length > 0) {
        return current.children.reduce(this.recursiveReducer, accumulator)
      }
      return accumulator || !current.masked
    },

    recursiveReducerStrict (accumulator, current) {
      if (current.children.length > 0) {
        return current.children.reduce(this.recursiveReducerStrict, accumulator)
      }

      return accumulator && !current.masked
    },

    retrieveBranchMask (children, list) {
      for (let i = 0; i < children.length; i++) {
        if (children[i].children.length > 0) {
          const rootChecked = children[i].children.reduce(this.recursiveReducer, false)

          list.push(rootChecked ? '1' : '0')

          if (rootChecked) {
            this.retrieveBranchMask(children[i].children, list)
          }
        } else {
          list.push(children[i].masked ? '0' : '1')
        }
      }

      return list
    },

    /**
     * Apply same state to children.
     */
    updateBranchState (children, state) {
      for (let i = 0; i < children.length; i++) {
        children[i].masked = state

        if (children[i].children.length > 0) {
          this.updateBranchState(children[i].children, state)
        }
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// ../../../../resources/js/components/CheckableTreeViewItem.vue